import type { ITabBarConfig } from './tab-bar';

/** Скалярные значения для метаданных (совместимо с аналитикой/платежами ядра). */
export type TBridgeScalar = string | number | boolean | null;

/** Семантический тип уведомления, показываемого оболочкой (toast). */
export type TBridgeNotificationType = 'success' | 'info' | 'warn' | 'error';

/**
 * Причина закрытия сервиса из web. Оболочка показывает её как уведомление (toast)
 * соответствующего типа — чтобы пользователю было понятно, почему его «выбросило».
 */
export interface ICloseReason {
  type: TBridgeNotificationType;
  message: string;
}

/**
 * Страницы оболочки, которые сервис может открыть через `navigation.openHostPage`.
 * Сервис передаёт смысловой id, а не маршрут: оболочка и нативка сами сопоставляют его
 * со своими экранами, поэтому один вызов работает на обеих платформах.
 */
export const HOST_PAGES = {
  Support: 'support',
  SupportRequests: 'supportRequests',
  SupportRequestCreate: 'supportRequestCreate'
} as const;

export type THostPage = (typeof HOST_PAGES)[keyof typeof HOST_PAGES];

const HOST_PAGE_SET: ReadonlySet<unknown> = new Set(Object.values(HOST_PAGES));

/** Известна ли оболочке страница. Payload приходит из сервиса — значение не доверенное. */
export function isHostPage(value: unknown): value is THostPage {
  return HOST_PAGE_SET.has(value);
}

/** Системные действия (сервис → оболочка). Доменные действия сервис объявляет отдельно. */
export const SYSTEM_ACTIONS = {
  Ready: 'bridge.ready',
  NavigationNavigate: 'navigation.navigate',
  NavigationBack: 'navigation.back',
  NavigationOpenMiniApp: 'navigation.openMiniApp',
  NavigationOpenHostPage: 'navigation.openHostPage',
  NavigationClose: 'navigation.close',
  NavigationSetCanGoBack: 'navigation.setCanGoBack',
  AuthGetStatus: 'auth.getStatus',
  AuthGetUser: 'auth.getUser',
  AuthHasScope: 'auth.hasScope',
  SsoAuthorize: 'sso.authorize',
  SaveCity: 'city.save',
  LocationGetCurrentPosition: 'location.getCurrentPosition',
  PaymentsPay: 'payments.pay',
  AnalyticsTrack: 'analytics.track',
  UiSetTitle: 'ui.setTitle',
  UiSetTabBarVisible: 'ui.setTabBarVisible',
  UiSetTabBar: 'ui.setTabBar',
  UiOpenExternal: 'ui.openExternal',
  UiShare: 'ui.share',
  UiHaptics: 'ui.haptics',
  Log: 'log'
} as const;

export type TSystemActionName =
  (typeof SYSTEM_ACTIONS)[keyof typeof SYSTEM_ACTIONS];

/** Типы payload системных действий. */
export interface ISystemActionPayloads {
  'bridge.ready': { sdkVersion?: string } | null;
  'navigation.navigate': { route: string; params?: Record<string, unknown> };
  'navigation.back': null;
  'navigation.openMiniApp': {
    miniAppId: string;
    params?: Record<string, unknown>;
  };
  /**
   * Открыть страницу оболочки (техподдержка и т.п.) вместо сервиса (RPC). Ответ — пустой
   * при успехе; неизвестная оболочке страница — ошибка `HANDLER_FAILED`.
   */
  'navigation.openHostPage': { page: THostPage };
  'navigation.close': { reason?: ICloseReason } | null;
  'navigation.setCanGoBack': { value: boolean };
  'auth.getStatus': null;
  'auth.getUser': null;
  'auth.hasScope': { scope: string };
  'sso.authorize': null;
  'city.save': { uuid: string; name: string };
  'location.getCurrentPosition': null;
  'payments.pay': {
    amount: number;
    currency: string;
    description?: string;
    metadata?: Record<string, TBridgeScalar>;
  };
  'analytics.track': { event: string; params?: Record<string, TBridgeScalar> };
  'ui.setTitle': { title: string };
  'ui.setTabBarVisible': { value: boolean };
  'ui.setTabBar': ITabBarConfig;
  'ui.openExternal': { url: string };
  'ui.share': { message?: string; url?: string; title?: string };
  'ui.haptics': {
    type?: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';
  };
  log: {
    level?: 'log' | 'info' | 'warn' | 'error';
    message: string;
    data?: unknown;
  };
}

const SYSTEM_ACTION_SET: ReadonlySet<string> = new Set(
  Object.values(SYSTEM_ACTIONS)
);

/** Действия, ожидающие ответа (RPC). */
const RPC_ACTION_SET: ReadonlySet<string> = new Set<string>([
  SYSTEM_ACTIONS.NavigationOpenHostPage,
  SYSTEM_ACTIONS.AuthGetStatus,
  SYSTEM_ACTIONS.AuthGetUser,
  SYSTEM_ACTIONS.AuthHasScope,
  SYSTEM_ACTIONS.SsoAuthorize,
  SYSTEM_ACTIONS.LocationGetCurrentPosition,
  SYSTEM_ACTIONS.PaymentsPay
]);

export function isSystemAction(action: string): action is TSystemActionName {
  return SYSTEM_ACTION_SET.has(action);
}

export function isRpcAction(action: string): boolean {
  return RPC_ACTION_SET.has(action);
}
